import { FC, Fragment } from "react";
import { Box, Typography } from "@mui/material";

import { IMessage } from "../../../../services";
import Column from "../../../ui/core/Column";
import Avatar from "../../../ui/Avatar";
import Row from "../../../ui/core/Row";
import ChatMessageFileBox from "./ChatMessageFileBox";
import { formatInstant } from "../../../helpers/dataHelper";

interface ChatUserMessageProps {
  message: IMessage;
  isCrossUserMessage?: boolean;
  userAvatar?: string;
}

const messageCreatedAtLabel = (message: IMessage): string => {
  const createdAt = message.created_at;

  if (createdAt != null && String(createdAt).trim() !== "") {
    return formatInstant(createdAt);
  }

  return "";
};

const ChatUserMessage: FC<ChatUserMessageProps> = ({ message, isCrossUserMessage, userAvatar }) => {
  const text =
    typeof message?.data === "string"
      ? message?.data?.split("\n")
      : // This case should never happen, but in case  it does, stringify the data
        [JSON.stringify(message?.data)];
  const files = message?.files;
  const messageDate = messageCreatedAtLabel(message);

  return (
    <Row
      sx={{
        alignItems: "flex-end",
        justifyContent: "flex-end",
        height: "auto",
        gridGap: "10px",
        maxWidth: "60rem",
        width: "100%",
        marginRight: "auto",
        marginLeft: "auto",
        marginBottom: "25px",
      }}
    >
      <Column
        sx={{
          position: "relative",
          gridGap: "10px",
          width: "fit-content",
          maxWidth: "100%",
          minWidth: 0,
          alignItems: "flex-end",
          "&:hover [data-message-actions]": { opacity: 1 },
        }}
      >
        {/* Uploaded files */}
        {files && files?.length > 0
          ? files.map((file, index) => (
              <ChatMessageFileBox key={file.id} file={file} index={index} />
            ))
          : null}

        {/* Message text */}
        {message?.data && text && text?.length > 0 ? (
          <Box
            sx={{
              display: "inline-block",
              textAlign: "left",
              borderRadius: "12px",
              border: "1px solid",
              borderColor: "#e5e7eb",
              backgroundColor: "#e5e7eb",
              px: 1.5,
              py: 1,
              width: "fit-content",
              maxWidth: "min(56.7rem, 100%)",
              minWidth: 0,
              mb: 0,
              ml: "42px",
            }}
          >
            {text?.map((line, index, array) => (
              <Fragment key={index}>
                <Typography
                  component={"span"}
                  sx={{
                    whiteSpace: "pre-line",
                    overflowWrap: "break-word",
                    wordBreak: "break-word",
                    fontWeight: 500,
                    padding: "0px",
                  }}
                >
                  {line}
                </Typography>
                {index < array.length - 1 && <br />}
              </Fragment>
            )) || "--"}
          </Box>
        ) : null}

        {messageDate ? (
          <Box
            data-message-actions
            sx={{
              position: "absolute",
              top: "100%",
              right: 0,
              mt: "4px",
              opacity: 0,
              transition: "opacity 200ms ease-in-out",
              whiteSpace: "nowrap",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {messageDate}
            </Typography>
          </Box>
        ) : null}
      </Column>

      <Avatar
        sender="me"
        isCrossUserMessage={isCrossUserMessage}
        userPictureUrl={ userAvatar || message?.metadata?.user_picture}
      />
    </Row>
  );
};

export default ChatUserMessage;
